/**
 * Release 2.2B — 6-step Deal Wizard.
 * Route: /app/deals/new
 * - Step 1 Overview (title, reference, type, description, internal notes)
 * - Step 2 Parties (multiple parties + optional contact person + inline new company)
 * - Step 3 Goods & Commercial terms
 * - Step 4 Delivery & Shipping
 * - Step 5 Payment & Banking (conditional fields)
 * - Step 6 Review & Create (or Save as draft)
 * Persists in-progress state to localStorage for refresh safety. Prevents
 * duplicate submits with an in-flight guard.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Save, Plus, X, Trash2, Check } from "lucide-react";
import {
  DEAL_TYPES_NEW, PARTY_ROLES, INCOTERMS_RULES, INCOTERMS_VERSIONS,
  PAYMENT_METHODS, CURRENCIES, TRANSPORT_MODES, QUANTITY_UNITS,
} from "@/lib/dealsMeta";

const DRAFT_KEY = "td_deal_wizard_draft_v1";

const EMPTY_PARTY = { role: "buyer", counterparty_id: null, legal_name: "", contact_name: "", contact_email: "", notes: "" };

const EMPTY_STATE = {
  title: "",
  reference: "",
  deal_type: "commodity_trade",
  description: "",
  internal_notes: "",
  parties: [],
  goods: {
    commodity: "", description: "", specification: "", origin: "",
    quantity: "", unit: "MT", tolerance: "",
    unit_price: "", price_basis: "", packaging: "", inspection: "", additional_terms: "",
  },
  currency: "USD",
  total_value: "",
  total_value_manual: false,
  delivery: {
    incoterms: "", incoterms_version: "Incoterms 2020",
    named_place: "", country: "", loading_port: "", discharge_port: "",
    shipment_window: "", partial_allowed: false, transshipment_allowed: false,
    transport_mode: "Sea", vessel: "", instructions: "",
  },
  payment: {
    method: "", schedule: "", advance_pct: "", balance: "",
    instrument: "", issuing_bank: "", advising_bank: "",
    amount: "", currency: "", validity: "", presentation_period: "",
    confirmation_required: false, transferable: false, notes: "",
  },
};

export default function DealWizard() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [state, setState] = useState(EMPTY_STATE);
  const [step, setStep] = useState(0);
  const [counterparties, setCounterparties] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNewCo, setShowNewCo] = useState(false);
  const [newCoIndex, setNewCoIndex] = useState(null);
  const [newCo, setNewCo] = useState({ legal_name: "", counterparty_type: "buyer", country: "" });
  const submittedRef = useRef(false);
  const initialisedRef = useRef(false);

  // Load counterparties + suggested reference; hydrate from localStorage if present.
  useEffect(() => {
    (async () => {
      try {
        const [cps, ref] = await Promise.all([api.listCounterparties(), api.dealsNextReference()]);
        setCounterparties(cps);
        const raw = localStorage.getItem(DRAFT_KEY);
        if (raw) {
          try {
            const saved = JSON.parse(raw);
            setState({ ...EMPTY_STATE, ...saved });
          } catch { /* corrupted — ignore */ }
        } else {
          setState((s) => ({ ...s, reference: ref.reference }));
        }
      } catch (e) { /* boot silently */ }
      initialisedRef.current = true;
    })();
  }, []);

  // Persist to localStorage on each change (once boot complete).
  useEffect(() => {
    if (!initialisedRef.current) return;
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(state)); } catch { /* no-op */ }
  }, [state]);

  // Warn on tab close if there are unsaved edits (title present and no submit yet).
  useEffect(() => {
    const hasDirty = state.title || state.parties.length > 0 || state.goods.commodity;
    const beforeUnload = (e) => {
      if (submittedRef.current || !hasDirty) return;
      e.preventDefault(); e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [state]);

  // SPA back-navigation guard. Pushes a duplicate entry so browser Back fires
  // popstate on the wizard instead of navigating away silently. When the wizard
  // is dirty, ask for confirmation before actually leaving.
  useEffect(() => {
    const hasDirty = () => !!(state.title || state.parties.length > 0 || state.goods.commodity);
    window.history.pushState({ tdWiz: true }, "");
    const onPop = (e) => {
      if (submittedRef.current) return;
      if (!hasDirty()) return; // no protection needed, allow the natural back
      // The browser has just popped the sentinel entry. Restore it so the user
      // stays on the wizard until they confirm.
      window.history.pushState({ tdWiz: true }, "");
      if (window.confirm(t("dw_unsaved_confirm"))) {
        submittedRef.current = true;
        localStorage.removeItem(DRAFT_KEY);
        nav("/app/deals");
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [nav, t, state]);

  const upd = (patch) => setState((s) => ({ ...s, ...patch }));
  const updNested = (section, patch) => setState((s) => ({ ...s, [section]: { ...s[section], ...patch } }));
  const updParty = (idx, patch) => setState((s) => ({
    ...s,
    parties: s.parties.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
  }));
  const addParty = () => setState((s) => ({ ...s, parties: [...s.parties, { ...EMPTY_PARTY }] }));
  const removeParty = (idx) => setState((s) => ({ ...s, parties: s.parties.filter((_, i) => i !== idx) }));

  // Auto-calc total = qty * unit_price when both numeric and manual override off.
  const calculatedTotal = useMemo(() => {
    const q = parseFloat(String(state.goods.quantity).replace(/[^\d.,-]/g, "").replace(",", "."));
    const u = parseFloat(String(state.goods.unit_price).replace(/[^\d.,-]/g, "").replace(",", "."));
    if (!isNaN(q) && !isNaN(u) && q > 0 && u > 0) return +(q * u).toFixed(2);
    return null;
  }, [state.goods.quantity, state.goods.unit_price]);

  useEffect(() => {
    if (!state.total_value_manual && calculatedTotal != null) {
      setState((s) => ({ ...s, total_value: String(calculatedTotal) }));
    }
  }, [calculatedTotal, state.total_value_manual]);

  const openNewCo = (idx) => {
    const currentRole = state.parties[idx]?.role || "other";
    setNewCoIndex(idx);
    setNewCo({ legal_name: "", counterparty_type: currentRole, country: "" });
    setShowNewCo(true);
  };

  const saveNewCo = async () => {
    if (!newCo.legal_name.trim()) return;
    try {
      const created = await api.createCounterparty(newCo);
      setCounterparties((prev) => [...prev, created].sort((a, b) => a.legal_name.localeCompare(b.legal_name)));
      if (newCoIndex != null) {
        updParty(newCoIndex, { counterparty_id: created.id, legal_name: created.legal_name });
      }
      setShowNewCo(false);
    } catch (e) {
      toast.error(t("err_generic_save"));
    }
  };

  const buildPayload = () => ({
    title: state.title.trim(),
    counterparty: state.parties.find((p) => p.role === "buyer")?.legal_name
                 || state.parties[0]?.legal_name || "",
    deal_type: state.deal_type,
    reference: state.reference.trim() || null,
    description: state.description || null,
    internal_notes: state.internal_notes || null,
    parties: state.parties
      .filter((p) => p.legal_name || p.counterparty_id)
      .map((p) => ({
        role: p.role,
        counterparty_id: p.counterparty_id || null,
        legal_name: p.legal_name || null,
        contact_name: p.contact_name || null,
        contact_email: p.contact_email || null,
        notes: p.notes || null,
      })),
    currency: state.currency || null,
    total_value: state.total_value !== "" && !isNaN(parseFloat(state.total_value))
      ? parseFloat(state.total_value) : null,
    total_value_manual: !!state.total_value_manual,
    parameters: {
      goods: state.goods,
      delivery: state.delivery,
      payment: state.payment,
    },
  });

  const submit = async (asDraft) => {
    if (submittedRef.current) return;
    if (!state.title.trim()) {
      toast.error(t("dw_title_lbl"));
      setStep(0);
      return;
    }
    submittedRef.current = true;
    setSaving(true);
    try {
      const payload = { ...buildPayload(), status: asDraft ? "draft" : "active", stage: "initial_enquiry" };
      const created = await api.createDeal(payload);
      localStorage.removeItem(DRAFT_KEY);
      toast.success(asDraft ? t("ok_draft_saved") : t("ok_deal_created"));
      nav(`/app/deals/${created.id}`);
    } catch (e) {
      submittedRef.current = false;
      setSaving(false);
      const detail = e?.response?.data?.detail;
      if (detail === "reference_taken") toast.error(t("err_reference_taken"));
      else toast.error(t("err_generic_save"));
    }
  };

  const steps = [
    { key: "overview",  label: t("dw_step1") },
    { key: "parties",   label: t("dw_step2") },
    { key: "goods",     label: t("dw_step3") },
    { key: "delivery",  label: t("dw_step4") },
    { key: "payment",   label: t("dw_step5") },
    { key: "review",    label: t("dw_step6") },
  ];

  const isLast = step === steps.length - 1;

  const goBack = () => {
    if (step === 0) {
      if (!window.confirm(t("dw_unsaved_confirm"))) return;
      submittedRef.current = true;
      localStorage.removeItem(DRAFT_KEY);
      nav("/app/deals");
    } else setStep((s) => s - 1);
  };

  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-5xl" data-testid="deal-wizard">
      <button onClick={goBack} data-testid="dw-back-btn"
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--td-text-2)] hover:text-[var(--td-gold)] mb-6 transition-colors">
        <ArrowLeft size={13} /> {step === 0 ? t("dd_back") : t("dw_back")}
      </button>

      <div className="mb-8">
        <div className="kicker mb-3">— {t("dw_page_title")}</div>
        <h1 className="font-serif text-4xl leading-tight mb-2">{steps[step].label}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl">{t("dw_page_sub")}</p>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-8" data-testid="dw-progress">
        {steps.map((s, i) => (
          <div key={s.key} className="flex-1 h-1 transition-colors"
               style={{ background: i <= step ? "var(--td-gold)" : "var(--td-border)" }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 mb-8 font-mono text-[10px] uppercase tracking-widest">
        {steps.map((s, i) => (
          <span key={s.key} className={i === step ? "text-[var(--td-gold)]" : "text-[var(--td-text-3)]"}>
            {i + 1}. {s.label}
          </span>
        ))}
      </div>

      <div className="card-surface p-8 mb-6">
        {step === 0 && <OverviewStep state={state} upd={upd} t={t} />}
        {step === 1 && <PartiesStep state={state} upd={upd} updParty={updParty} addParty={addParty} removeParty={removeParty}
                                     counterparties={counterparties} openNewCo={openNewCo} t={t} />}
        {step === 2 && <GoodsStep state={state} updNested={updNested} upd={upd} calculatedTotal={calculatedTotal} t={t} />}
        {step === 3 && <DeliveryStep state={state} updNested={updNested} t={t} />}
        {step === 4 && <PaymentStep state={state} updNested={updNested} t={t} />}
        {step === 5 && <ReviewStep state={state} t={t} />}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => submit(true)}
          disabled={saving || !state.title.trim()}
          data-testid="dw-save-draft"
          className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40"
        >
          <Save size={13} /> {t("dw_save_draft")}
        </button>
        <div className="flex items-center gap-3">
          {step > 0 && (
            <button onClick={() => setStep((s) => s - 1)} data-testid="dw-prev"
                    className="btn-ghost inline-flex items-center gap-2">
              <ArrowLeft size={13} /> {t("dw_back")}
            </button>
          )}
          {!isLast ? (
            <button onClick={() => setStep((s) => s + 1)} data-testid="dw-next"
                    className="btn-gold inline-flex items-center gap-2">
              {t("dw_next")} <ArrowRight size={13} />
            </button>
          ) : (
            <button onClick={() => submit(false)} disabled={saving || !state.title.trim()}
                    data-testid="dw-create"
                    className="btn-gold inline-flex items-center gap-2 disabled:opacity-40">
              <Check size={13} /> {saving ? t("dw_saving") : t("dw_create")}
            </button>
          )}
        </div>
      </div>

      {showNewCo && (
        <NewCompanyModal
          data={newCo}
          onChange={setNewCo}
          onCancel={() => setShowNewCo(false)}
          onSave={saveNewCo}
          t={t}
        />
      )}
    </div>
  );
}

// ---------- Steps ----------

function OverviewStep({ state, upd, t }) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_title_lbl")} *</label>
        <input data-testid="dw-title" required className="td-input" value={state.title}
               onChange={(e) => upd({ title: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_reference_lbl")}</label>
        <input data-testid="dw-reference" className="td-input font-mono" value={state.reference}
               onChange={(e) => upd({ reference: e.target.value })} />
        <div className="text-[11px] text-[var(--td-text-3)] mt-1">{t("dw_reference_hint")}</div>
      </div>
      <div>
        <label className="td-label">{t("dw_type_lbl")} *</label>
        <select data-testid="dw-type" className="td-input" value={state.deal_type}
                onChange={(e) => upd({ deal_type: e.target.value })}>
          {DEAL_TYPES_NEW.map((k) => <option key={k} value={k}>{t(`dt_${k}`)}</option>)}
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_description_lbl")}</label>
        <textarea data-testid="dw-description" rows={2} className="td-input resize-none"
                  value={state.description} onChange={(e) => upd({ description: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_internal_notes_lbl")}</label>
        <textarea data-testid="dw-internal-notes" rows={2} className="td-input resize-none"
                  value={state.internal_notes} onChange={(e) => upd({ internal_notes: e.target.value })} />
      </div>
    </div>
  );
}

function PartiesStep({ state, updParty, addParty, removeParty, counterparties, openNewCo, t }) {
  if (state.parties.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-[var(--td-text-2)] mb-6">{t("dw_party_empty")}</p>
        <button onClick={addParty} data-testid="dw-add-party" className="btn-gold inline-flex items-center gap-2">
          <Plus size={13} /> {t("dw_add_party")}
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      {state.parties.map((p, idx) => {
        const cp = counterparties.find((c) => c.id === p.counterparty_id);
        const contacts = cp?.contacts || [];
        return (
          <div key={idx} data-testid={`dw-party-${idx}`} className="border border-[var(--td-border)] p-5 rounded-sm">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="grid md:grid-cols-2 gap-4 flex-1">
                <div>
                  <label className="td-label">{t("dw_party_role")}</label>
                  <select data-testid={`dw-party-${idx}-role`} className="td-input" value={p.role}
                          onChange={(e) => updParty(idx, { role: e.target.value })}>
                    {PARTY_ROLES.map((r) => <option key={r} value={r}>{t(`pr_${r}`)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="td-label">{t("dw_party_company")}</label>
                  <select data-testid={`dw-party-${idx}-company`} className="td-input"
                          value={p.counterparty_id || ""}
                          onChange={(e) => {
                            const id = e.target.value;
                            if (id === "__new__") { openNewCo(idx); return; }
                            const c = counterparties.find((x) => x.id === id);
                            updParty(idx, { counterparty_id: id || null, legal_name: c ? c.legal_name : p.legal_name, contact_name: "" });
                          }}>
                    <option value="">— {t("dw_party_contact_none").replace(/[—-]\s*/g, "")} —</option>
                    {counterparties.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
                    <option value="__new__">{t("dw_party_new_company")}</option>
                  </select>
                  {!p.counterparty_id && (
                    <input placeholder={t("dw_new_co_legal")} className="td-input mt-2"
                           data-testid={`dw-party-${idx}-name`}
                           value={p.legal_name}
                           onChange={(e) => updParty(idx, { legal_name: e.target.value })} />
                  )}
                </div>
                {contacts.length > 0 && (
                  <div>
                    <label className="td-label">{t("dw_party_contact")}</label>
                    <select data-testid={`dw-party-${idx}-contact`} className="td-input"
                            value={p.contact_name || ""}
                            onChange={(e) => {
                              const name = e.target.value;
                              const c = contacts.find((x) => x.name === name);
                              updParty(idx, { contact_name: name || "", contact_email: c?.email || "" });
                            }}>
                      <option value="">{t("dw_party_contact_none")}</option>
                      {contacts.map((c, i) => <option key={i} value={c.name}>{c.name}{c.role ? ` — ${c.role}` : ""}</option>)}
                    </select>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="td-label">{t("dw_party_notes")}</label>
                  <input className="td-input" data-testid={`dw-party-${idx}-notes`}
                         value={p.notes} onChange={(e) => updParty(idx, { notes: e.target.value })} />
                </div>
              </div>
              <button onClick={() => removeParty(idx)} data-testid={`dw-party-${idx}-remove`}
                      aria-label={t("dw_party_remove")}
                      className="text-[var(--td-text-3)] hover:text-red-400 shrink-0 p-1.5"
                      title={t("dw_party_remove")}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
      <button onClick={addParty} data-testid="dw-add-party" className="btn-ghost inline-flex items-center gap-2">
        <Plus size={13} /> {t("dw_add_party")}
      </button>
    </div>
  );
}

function GoodsStep({ state, updNested, upd, calculatedTotal, t }) {
  const g = state.goods;
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_goods_commodity")}</label>
        <input data-testid="dw-goods-commodity" className="td-input" value={g.commodity}
               onChange={(e) => updNested("goods", { commodity: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_goods_desc")}</label>
        <textarea rows={2} className="td-input resize-none" value={g.description}
                  onChange={(e) => updNested("goods", { description: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_goods_spec")}</label>
        <textarea rows={2} className="td-input resize-none" value={g.specification}
                  onChange={(e) => updNested("goods", { specification: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_goods_origin")}</label>
        <input className="td-input" value={g.origin} onChange={(e) => updNested("goods", { origin: e.target.value })} />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="td-label">{t("dw_goods_quantity")}</label>
          <input data-testid="dw-goods-quantity" className="td-input" inputMode="decimal" value={g.quantity}
                 onChange={(e) => updNested("goods", { quantity: e.target.value })} />
        </div>
        <div>
          <label className="td-label">{t("dw_goods_unit")}</label>
          <select className="td-input" value={g.unit} onChange={(e) => updNested("goods", { unit: e.target.value })}>
            {QUANTITY_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <div>
          <label className="td-label">{t("dw_goods_tolerance")}</label>
          <input className="td-input" inputMode="decimal" value={g.tolerance}
                 onChange={(e) => updNested("goods", { tolerance: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="td-label">{t("dw_goods_unit_price")}</label>
        <input data-testid="dw-goods-unit-price" className="td-input" inputMode="decimal" value={g.unit_price}
               onChange={(e) => updNested("goods", { unit_price: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_goods_price_basis")}</label>
        <input className="td-input" value={g.price_basis}
               onChange={(e) => updNested("goods", { price_basis: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_currency")}</label>
        <select data-testid="dw-currency" className="td-input" value={state.currency}
                onChange={(e) => upd({ currency: e.target.value })}>
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="td-label">{t("dw_total_value")}</label>
        <div className="flex gap-2">
          <input data-testid="dw-total-value" className="td-input font-mono" value={state.total_value}
                 onChange={(e) => upd({ total_value: e.target.value, total_value_manual: true })} />
          {state.total_value_manual && calculatedTotal != null && (
            <button type="button" onClick={() => upd({ total_value_manual: false, total_value: String(calculatedTotal) })}
                    className="btn-ghost !px-3 text-[10px] font-mono uppercase tracking-widest">
              {t("dw_total_recalc")}
            </button>
          )}
        </div>
        <div className="text-[11px] text-[var(--td-text-3)] mt-1">
          {state.total_value_manual ? t("dw_total_manual") : (calculatedTotal != null ? t("dw_total_calc") : "")}
        </div>
      </div>
      <div>
        <label className="td-label">{t("dw_goods_packaging")}</label>
        <input className="td-input" value={g.packaging}
               onChange={(e) => updNested("goods", { packaging: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_goods_inspection")}</label>
        <input className="td-input" value={g.inspection}
               onChange={(e) => updNested("goods", { inspection: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_goods_addl_terms")}</label>
        <textarea rows={2} className="td-input resize-none" value={g.additional_terms}
                  onChange={(e) => updNested("goods", { additional_terms: e.target.value })} />
      </div>
    </div>
  );
}

function DeliveryStep({ state, updNested, t }) {
  const d = state.delivery;
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div>
        <label className="td-label">{t("dw_delivery_incoterms")}</label>
        <select data-testid="dw-incoterms" className="td-input" value={d.incoterms}
                onChange={(e) => updNested("delivery", { incoterms: e.target.value })}>
          <option value="">—</option>
          {INCOTERMS_RULES.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_incoterms_ver")}</label>
        <select className="td-input" value={d.incoterms_version}
                onChange={(e) => updNested("delivery", { incoterms_version: e.target.value })}>
          {INCOTERMS_VERSIONS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_named_place")}{d.incoterms ? " *" : ""}</label>
        <input data-testid="dw-named-place" className="td-input" value={d.named_place}
               onChange={(e) => updNested("delivery", { named_place: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_country")}</label>
        <input className="td-input" value={d.country}
               onChange={(e) => updNested("delivery", { country: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_load")}</label>
        <input className="td-input" value={d.loading_port}
               onChange={(e) => updNested("delivery", { loading_port: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_discharge")}</label>
        <input className="td-input" value={d.discharge_port}
               onChange={(e) => updNested("delivery", { discharge_port: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_window")}</label>
        <input data-testid="dw-window" className="td-input" value={d.shipment_window}
               onChange={(e) => updNested("delivery", { shipment_window: e.target.value })}
               placeholder="e.g. Jan–Mar 2026" />
      </div>
      <div>
        <label className="td-label">{t("dw_delivery_mode")}</label>
        <select className="td-input" value={d.transport_mode}
                onChange={(e) => updNested("delivery", { transport_mode: e.target.value })}>
          {TRANSPORT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-6">
        <label className="inline-flex items-center gap-2 text-sm text-[var(--td-text-2)]">
          <input type="checkbox" checked={d.partial_allowed}
                 onChange={(e) => updNested("delivery", { partial_allowed: e.target.checked })} />
          {t("dw_delivery_partial")}
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-[var(--td-text-2)]">
          <input type="checkbox" checked={d.transshipment_allowed}
                 onChange={(e) => updNested("delivery", { transshipment_allowed: e.target.checked })} />
          {t("dw_delivery_transship")}
        </label>
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_delivery_vessel")}</label>
        <input className="td-input" value={d.vessel}
               onChange={(e) => updNested("delivery", { vessel: e.target.value })} />
      </div>
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_delivery_instructions")}</label>
        <textarea rows={2} className="td-input resize-none" value={d.instructions}
                  onChange={(e) => updNested("delivery", { instructions: e.target.value })} />
      </div>
    </div>
  );
}

function PaymentStep({ state, updNested, t }) {
  const p = state.payment;
  const isLC = /LC|SBLC|Bank Guarantee|Documentary Collection/i.test(p.method || "");
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div>
        <label className="td-label">{t("dw_pay_method")}</label>
        <select data-testid="dw-pay-method" className="td-input" value={p.method}
                onChange={(e) => updNested("payment", { method: e.target.value })}>
          <option value="">—</option>
          {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div>
        <label className="td-label">{t("dw_pay_schedule")}</label>
        <input className="td-input" value={p.schedule}
               onChange={(e) => updNested("payment", { schedule: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_pay_advance_pct")}</label>
        <input className="td-input" inputMode="decimal" value={p.advance_pct}
               onChange={(e) => updNested("payment", { advance_pct: e.target.value })} />
      </div>
      <div>
        <label className="td-label">{t("dw_pay_balance")}</label>
        <input className="td-input" value={p.balance}
               onChange={(e) => updNested("payment", { balance: e.target.value })} />
      </div>
      {isLC && (
        <>
          <div>
            <label className="td-label">{t("dw_pay_instrument")}</label>
            <input className="td-input" value={p.instrument}
                   onChange={(e) => updNested("payment", { instrument: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("dw_pay_issuing_bank")}{isLC ? " *" : ""}</label>
            <input data-testid="dw-issuing-bank" className="td-input" value={p.issuing_bank}
                   onChange={(e) => updNested("payment", { issuing_bank: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("dw_pay_advising_bank")}</label>
            <input className="td-input" value={p.advising_bank}
                   onChange={(e) => updNested("payment", { advising_bank: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("dw_pay_amount")}</label>
            <input className="td-input" inputMode="decimal" value={p.amount}
                   onChange={(e) => updNested("payment", { amount: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("dw_pay_currency")}</label>
            <select className="td-input" value={p.currency}
                    onChange={(e) => updNested("payment", { currency: e.target.value })}>
              <option value="">—</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="td-label">{t("dw_pay_validity")}</label>
            <input className="td-input" value={p.validity}
                   onChange={(e) => updNested("payment", { validity: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("dw_pay_presentation")}</label>
            <input className="td-input" value={p.presentation_period}
                   onChange={(e) => updNested("payment", { presentation_period: e.target.value })} />
          </div>
          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm text-[var(--td-text-2)]">
              <input type="checkbox" checked={p.confirmation_required}
                     onChange={(e) => updNested("payment", { confirmation_required: e.target.checked })} />
              {t("dw_pay_confirmed")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-[var(--td-text-2)]">
              <input type="checkbox" checked={p.transferable}
                     onChange={(e) => updNested("payment", { transferable: e.target.checked })} />
              {t("dw_pay_transferable")}
            </label>
          </div>
        </>
      )}
      <div className="md:col-span-2">
        <label className="td-label">{t("dw_pay_notes")}</label>
        <textarea rows={2} className="td-input resize-none" value={p.notes}
                  onChange={(e) => updNested("payment", { notes: e.target.value })} />
      </div>
    </div>
  );
}

function ReviewStep({ state, t }) {
  const Line = ({ label, value }) => (
    <div className="flex items-baseline gap-4 py-1">
      <div className="td-label flex-shrink-0 w-40">{label}</div>
      <div className="text-sm text-[var(--td-text)]">{value || <span className="text-[var(--td-text-3)] italic">{t("dw_review_no_data")}</span>}</div>
    </div>
  );
  return (
    <div>
      <h3 className="font-serif text-2xl mb-1">{t("dw_review_title")}</h3>
      <p className="text-[var(--td-text-2)] mb-6 max-w-2xl">{t("dw_review_sub")}</p>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-2">{t("dw_step1")}</div>
          <Line label={t("dw_title_lbl")} value={state.title} />
          <Line label={t("dw_reference_lbl")} value={state.reference} />
          <Line label={t("dw_type_lbl")} value={t(`dt_${state.deal_type}`)} />
          <Line label={t("dw_description_lbl")} value={state.description} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-2">{t("dw_step2")}</div>
          {state.parties.length === 0 ? <div className="text-[var(--td-text-3)] italic">{t("dw_review_no_data")}</div> :
            <ul className="space-y-1 text-sm">
              {state.parties.map((p, i) => (
                <li key={i}><span className="font-mono text-[10px] uppercase text-[var(--td-text-3)]">{t(`pr_${p.role}`)}</span> — {p.legal_name || <em className="text-[var(--td-text-3)]">{t("dw_review_no_data")}</em>}</li>
              ))}
            </ul>}
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-2">{t("dw_step3")}</div>
          <Line label={t("dw_goods_commodity")} value={state.goods.commodity} />
          <Line label={t("dw_goods_quantity")} value={state.goods.quantity ? `${state.goods.quantity} ${state.goods.unit}` : ""} />
          <Line label={t("dw_goods_unit_price")} value={state.goods.unit_price} />
          <Line label={t("dw_total_value")} value={state.total_value ? `${state.currency} ${state.total_value}` : ""} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-2">{t("dw_step4")}</div>
          <Line label={t("dw_delivery_incoterms")} value={state.delivery.incoterms ? `${state.delivery.incoterms} ${state.delivery.incoterms_version}` : ""} />
          <Line label={t("dw_delivery_named_place")} value={state.delivery.named_place} />
          <Line label={t("dw_delivery_window")} value={state.delivery.shipment_window} />
        </div>
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-gold)] mb-2">{t("dw_step5")}</div>
          <Line label={t("dw_pay_method")} value={state.payment.method} />
          <Line label={t("dw_pay_issuing_bank")} value={state.payment.issuing_bank} />
          <Line label={t("dw_pay_schedule")} value={state.payment.schedule} />
        </div>
      </div>
    </div>
  );
}

function NewCompanyModal({ data, onChange, onCancel, onSave, t }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="card-surface w-full max-w-md p-6 relative">
        <button onClick={onCancel} aria-label={t("dw_new_co_cancel")} title={t("dw_new_co_cancel")}
                className="absolute top-3 right-3 text-[var(--td-text-3)] hover:text-[var(--td-text)]">
          <X size={16} aria-hidden="true" />
        </button>
        <div className="kicker mb-4">— {t("dw_party_new_company")}</div>
        <div className="space-y-4">
          <div>
            <label className="td-label">{t("dw_new_co_legal")} *</label>
            <input data-testid="dw-newco-name" required className="td-input"
                   value={data.legal_name}
                   onChange={(e) => onChange({ ...data, legal_name: e.target.value })} />
          </div>
          <div>
            <label className="td-label">{t("dw_new_co_type")}</label>
            <select className="td-input" value={data.counterparty_type}
                    onChange={(e) => onChange({ ...data, counterparty_type: e.target.value })}>
              {PARTY_ROLES.map((r) => <option key={r} value={r}>{t(`pr_${r}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="td-label">{t("dw_new_co_country")}</label>
            <input className="td-input" value={data.country}
                   onChange={(e) => onChange({ ...data, country: e.target.value })} />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="btn-ghost">{t("dw_new_co_cancel")}</button>
          <button onClick={onSave} data-testid="dw-newco-save" disabled={!data.legal_name.trim()}
                  className="btn-gold disabled:opacity-40">{t("dw_new_co_save")}</button>
        </div>
      </div>
    </div>
  );
}
